import { alpha, Box, Stack, Typography, useTheme } from "@mui/material";
import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

type PropsTypes = {
  children: ReactNode;
  title: string;
  route?: string;
  routeName?: string;
  description?: string;
  subtitle?: string;
};

const AuthenticationLayout: FC<PropsTypes> = ({
  children,
  title,
  route,
  routeName,
  description,
  subtitle,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 6,
        backgroundColor: "background.default",
        backgroundImage: isDark
          ? `radial-gradient(circle at 50% -10%, ${alpha(accent, 0.16)} 0%, transparent 55%)`
          : `radial-gradient(circle at 50% -10%, ${alpha(accent, 0.1)} 0%, transparent 55%)`,
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ width: "100%", maxWidth: 400 }}>
        <Box sx={{ textAlign: "center" }}>
          <Box
            component="img"
            src="/static/logo/listli_logo_full.png"
            alt="logo"
            sx={{
              maxWidth: 140,
              height: "auto",
              filter: isDark ? "brightness(0) invert(1)" : "none",
            }}
          />
        </Box>

        <Box
          sx={{
            width: "100%",
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            backgroundColor: "background.paper",
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: isDark
              ? "0 24px 48px -16px rgba(0, 0, 0, 0.5)"
              : "0 24px 48px -20px rgba(12, 10, 9, 0.12), 0 8px 16px -10px rgba(12, 10, 9, 0.06)",
          }}
        >
          <Stack spacing={0.75} sx={{ mb: 3.5, textAlign: "center" }}>
            <Typography variant="h3" sx={{ fontWeight: 650, letterSpacing: "-0.02em" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Stack>

          {children}
        </Box>

        {description && route && routeName && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            {description}{" "}
            <Link
              to={route}
              style={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              {routeName}
            </Link>
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default AuthenticationLayout;
